import { CalculationAttachmentsPanel } from '@/components/features/CalculationRequests/components/CalculationAttachmentsPanel';
import { CompleteCalculationModal } from '@/components/features/CalculationRequests/forms/CompleteCalculationModal';
import { EditCalculationSpecSheet } from '@/components/features/CalculationRequests/forms/EditCalculationSpecSheet';
import {
    ComplexityBadge,
    OverdueBadge,
    PhaseBadge,
    StatusBadge,
    userName,
} from '@/components/features/CalculationRequests/tables/calculation-request-badges';
import { InlineCalcStatusCell } from '@/components/features/CalculationRequests/tables/inline-cells/InlineCalcStatusCell';
import type { CalculationRequest, CalculationRequestActivityEvent, CalculationRequestOptions } from '@/components/features/CalculationRequests/types';
import UniversalComments from '@/components/global/comments/UniversalComments';
import { PersonSelect } from '@/components/global/PersonSelect';
import { SummaryMetricCard } from '@/components/ui/analytics/SummaryMetricCard';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/shadcn/alert-dialog';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/shadcn/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/shadcn/tabs';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { isTerminalStage } from '@/lib/calculation-request-stages';
import { settingLabel } from '@/lib/status-labels';
import { formatCurrency, formatDateForDisplay, useCan } from '@/lib/utils';
import { type User } from '@/types';
import { Head, Link, router, usePage } from '@inertiajs/react';
import { ArrowLeft, BarChart3, CheckCircle2, Clock, Copy, FileText, Pencil, Play, Send, Trash2, UserPlus } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';

interface ShowPageProps {
    request: CalculationRequest;
    events: CalculationRequestActivityEvent[];
    options: CalculationRequestOptions;
    /** Everyone active — drives @-mentions in comments. */
    users: Array<{ id: string; first_name?: string | null; last_name?: string | null }>;
    /** Active non-engineers: engineers already hold see-all, so adding one as a contributor grants nothing. */
    contributorCandidates: Array<{ id: string; first_name?: string | null; last_name?: string | null }>;
    currentUser: User;
    [key: string]: unknown;
}

export default function CalculationRequestShow() {
    const { props } = usePage<ShowPageProps>();
    const { request, events, options, users, contributorCandidates, currentUser } = props;
    const { t } = useTranslations();
    const { can, roles, isAdministrator } = useCan();
    const [completeOpen, setCompleteOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [editSpecOpen, setEditSpecOpen] = useState(false);

    /** The person who raised this request, as opposed to anyone who merely holds `.update`. */
    const isOwner = request.submitted_by_user_id === currentUser.id;

    /**
     * The specification belongs to engineering outright, and to whoever raised the
     * request only while it is still a draft — after that an engineer is pricing what
     * it says. Deliberately a role/ownership check rather than
     * `calculation-requests.update`, which all of sales holds; the endpoint agrees.
     */
    const canEditSpecification =
        isAdministrator || roles.includes('Engineer') || roles.includes('Engineers Manager') || (isOwner && request.stage === 'draft');

    const contributorIds = useMemo(() => (request.contributors ?? []).map((contributor) => contributor.id), [request.contributors]);

    /**
     * Candidates already exclude suspended users and engineers (server side). Anyone
     * currently on the request is merged in regardless, so an existing contributor is
     * always representable — and therefore removable — even if they later fall out of
     * the candidate set.
     */
    const contributorPeople = useMemo(() => {
        const byId = new Map(contributorCandidates.map((candidate) => [candidate.id, candidate]));
        (request.contributors ?? []).forEach((contributor) => byId.set(contributor.id, contributor));

        return Array.from(byId.values());
    }, [contributorCandidates, request.contributors]);

    const dash = t('calculation_requests.tables.none', '—');
    const canUpdate = can('calculation-requests.update');
    const canAssign = can('calculation-requests.assign');
    // A contributor is only extra visibility of this one request, so its owner hands
    // that out without holding the queue-wide `.assign` grant. The endpoint agrees.
    const canManageContributors = canAssign || isOwner;
    // Sales holds `.update` so they can submit and edit their own request; deciding
    // that engineering has started is a separate call, and the endpoint agrees.
    const canStart = can('calculation-requests.start');
    const canComplete = can('calculation-requests.complete');
    const canDelete = can('calculation-requests.delete');
    const canViewFinancials = can('analytics.view-financial');

    const breadcrumbs = [
        { title: t('calculation_requests.show.breadcrumbs.calculation_requests', 'Calculation Requests'), href: '/calculation-requests' },
        { title: request.request_number ?? t('calculation_requests.show.title', 'Calculation Request'), href: `/calculation-requests/${request.id}` },
    ];

    const patchStatus = async (status: string) => {
        router.patch(
            `/calculation-requests/${request.id}/status`,
            { status },
            {
                preserveScroll: true,
                onSuccess: () => toast.success(t('calculation_requests.index.messages.status_updated', 'Status updated')),
                // The server explains a refused start ("only engineers can…"); a
                // generic failure message would hide the actual reason.
                onError: (errors) =>
                    toast.error(errors.status ?? t('calculation_requests.index.messages.failed_update_status', 'Failed to update status')),
            },
        );
    };

    const assign = async (userId: string | null) => {
        router.patch(
            `/calculation-requests/${request.id}/assign`,
            { assigned_to_user_id: userId },
            {
                preserveScroll: true,
                onSuccess: () => toast.success(t('calculation_requests.index.messages.assignee_updated', 'Engineer updated')),
                onError: (errors) =>
                    toast.error(
                        errors.assigned_to_user_id ?? t('calculation_requests.index.messages.failed_update_assignee', 'Failed to update engineer'),
                    ),
            },
        );
    };

    /**
     * The picker toggles one person at a time, so translate the new id list back into
     * the single add/remove call the server exposes. Firing both would race.
     *
     * A refusal comes back as a 422 rather than a 403 so the picker survives it — which
     * means the message only reaches the user through onError.
     */
    const contributorFailed = (errors: Record<string, string>) =>
        toast.error(errors.user_id ?? t('calculation_requests.index.messages.failed_update_contributors', 'Failed to update contributors'));

    const syncContributors = (next: string[]) => {
        const added = next.find((id) => !contributorIds.includes(id));
        if (added !== undefined) {
            router.post(`/calculation-requests/${request.id}/contributors`, { user_id: added }, { preserveScroll: true, onError: contributorFailed });

            return;
        }

        const removed = contributorIds.find((id) => !next.includes(id));
        if (removed !== undefined) {
            router.delete(`/calculation-requests/${request.id}/contributors/${removed}`, {
                preserveScroll: true,
                onError: contributorFailed,
            });
        }
    };

    /**
     * Spec rows in two balanced columns, in the same order as the paper form.
     * Engineering edits them in place through the sheet; everyone else reads.
     */
    const specRows: Array<[string, string | null]> = [
        [t('calculation_requests.form.fields.system', 'System'), request.system],
        [t('calculation_requests.form.fields.color_exterior', 'Colour — exterior'), request.color_exterior],
        [t('calculation_requests.form.fields.color_interior', 'Colour — interior'), request.color_interior],
        [t('calculation_requests.form.fields.glass_g_value', 'Solar control (g)'), request.glass_g_value],
        [t('calculation_requests.form.fields.glass_light_transmittance', 'Light transmittance (Lt)'), request.glass_light_transmittance],
        [t('calculation_requests.form.fields.construction_u_value', 'Construction U-value'), request.construction_u_value],
        [t('calculation_requests.form.fields.infills_enamel', 'Infills / enamelled glass'), request.infills_enamel],
        [t('calculation_requests.form.fields.sound_insulation', 'Sound insulation'), request.sound_insulation],
        [t('calculation_requests.form.fields.water_tightness_class', 'Watertightness class'), request.water_tightness_class],
        [t('calculation_requests.form.fields.wind_load_resistance', 'Wind load resistance'), request.wind_load_resistance],
        [t('calculation_requests.form.fields.air_permeability_class', 'Air permeability class'), request.air_permeability_class],
    ];

    // Workflow affordances key off the lifecycle stage, never the status value —
    // statuses are admin-editable.
    const showSubmit = request.stage === 'draft' && canUpdate;
    const showStart = request.stage === 'queued' && canStart;
    const showComplete = !isTerminalStage(request.stage) && request.stage !== 'draft' && canComplete;

    // Only engineers can hold a calculation, so "assign to me" is offered only when the
    // current user is actually in the engineer pick list — the same set the server
    // validates against.
    const currentUserIsEngineer = options.engineers.some((engineer) => engineer.id === currentUser.id);
    const showAssignMe = canAssign && !request.assignee && currentUserIsEngineer;

    const calculated = request.calculated_amount === null || request.calculated_amount === undefined ? null : Number(request.calculated_amount);
    const hours = request.hours_spent === null || request.hours_spent === undefined ? null : Number(request.hours_spent);

    const statusLabel = settingLabel(t, 'calculation_request_statuses', request.status) || request.status;
    const restricted = t('calculation_requests.index.metrics.restricted', 'Restricted');
    const notCalculated = t('calculation_requests.show.kpi.not_calculated', 'Not calculated yet');
    const notYet = t('calculation_requests.show.timeline.not_yet', 'Not yet');
    const notLinked = t('calculation_requests.show.overview.not_linked', 'Not linked');

    /** The request's dates in lifecycle order, rendered as one strip under the KPIs. */
    const dateStrip: Array<{ label: string; value: ReactNode; badge?: ReactNode }> = [
        {
            label: t('calculation_requests.tables.created_at', 'Created'),
            value: request.created_at ? formatDateForDisplay(request.created_at) : dash,
        },
        {
            label: t('calculation_requests.tables.requested_calculation_date', 'Needed by'),
            value: request.requested_calculation_date ? formatDateForDisplay(request.requested_calculation_date) : dash,
            badge: request.is_overdue ? <OverdueBadge /> : null,
        },
        {
            label: t('calculation_requests.show.timeline.started', 'Started'),
            value: request.started_at ? formatDateForDisplay(request.started_at) : <span className="text-muted-foreground">{notYet}</span>,
        },
        {
            label: t('calculation_requests.show.timeline.completed', 'Completed'),
            value: request.completed_at ? formatDateForDisplay(request.completed_at) : <span className="text-muted-foreground">{notYet}</span>,
        },
    ];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={request.request_number ?? t('calculation_requests.show.title', 'Calculation Request')} />

            <div className="space-y-5 p-4 sm:p-6 lg:p-8">
                {/* ── Header: identity left, actions right ─────────────────────── */}
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2.5">
                            <h1 className="truncate text-2xl leading-tight font-semibold tracking-tight">
                                {request.request_number}
                                {request.object_name ? <span className="text-muted-foreground font-normal"> — {request.object_name}</span> : null}
                            </h1>
                            <StatusBadge value={request.status} options={options.statuses} />
                            {request.is_overdue ? <OverdueBadge /> : null}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => router.visit('/calculation-requests')}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            {t('calculation_requests.show.actions.back', 'Back to queue')}
                        </Button>
                        {showSubmit ? (
                            <Button size="sm" onClick={() => patchStatus(nextStatusForStage(options, 'queued'))}>
                                <Send className="mr-2 h-4 w-4" />
                                {t('calculation_requests.show.actions.submit', 'Submit to queue')}
                            </Button>
                        ) : null}
                        {showStart ? (
                            <Button variant="outline" size="sm" onClick={() => patchStatus(nextStatusForStage(options, 'working'))}>
                                <Play className="mr-2 h-4 w-4" />
                                {t('calculation_requests.show.actions.start', 'Start calculating')}
                            </Button>
                        ) : null}
                        {showAssignMe ? (
                            <Button variant="outline" size="sm" onClick={() => assign(String(currentUser.id))}>
                                <UserPlus className="mr-2 h-4 w-4" />
                                {t('calculation_requests.show.actions.assign_me', 'Assign to me')}
                            </Button>
                        ) : null}
                        {showComplete ? (
                            <Button size="sm" onClick={() => setCompleteOpen(true)}>
                                {t('calculation_requests.show.actions.complete', 'Record calculation')}
                            </Button>
                        ) : null}
                        {can('calculation-requests.create') ? (
                            <Button variant="outline" size="sm" onClick={() => router.post(`/calculation-requests/${request.id}/duplicate`)}>
                                <Copy className="mr-2 h-4 w-4" />
                                {t('calculation_requests.show.actions.duplicate', 'Duplicate')}
                            </Button>
                        ) : null}
                        {canDelete ? (
                            <Button
                                variant="outline"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                                onClick={() => setDeleteOpen(true)}
                            >
                                <Trash2 className="mr-2 h-4 w-4" />
                                {t('calculation_requests.show.actions.delete', 'Delete')}
                            </Button>
                        ) : null}
                    </div>
                </div>

                <Tabs defaultValue="details" className="space-y-0">
                    <TabsList>
                        <TabsTrigger value="details">{t('calculation_requests.show.tabs.details', 'Details')}</TabsTrigger>
                        <TabsTrigger value="attachments">
                            {t('calculation_requests.show.tabs.attachments', 'Files')}
                            {(request.attachments ?? []).length > 0 ? (
                                <Badge variant="outline" className="ml-2">
                                    {(request.attachments ?? []).length}
                                </Badge>
                            ) : null}
                        </TabsTrigger>
                        <TabsTrigger value="activity">{t('calculation_requests.show.tabs.activity', 'Activity')}</TabsTrigger>
                    </TabsList>

                    {/*
                     * Main column carries the tab panels; the sidebar stays pinned across
                     * tabs so the responsible people and dates never leave the screen.
                     */}
                    <div className="mt-4 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,3fr)_minmax(320px,1fr)]">
                        <div className="min-w-0 space-y-4">
                            <TabsContent value="details" className="mt-0 space-y-4">
                                {/* KPI row */}
                                <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                                    <SummaryMetricCard
                                        title={t('calculation_requests.show.kpi.contract_value', 'Contract value')}
                                        value={canViewFinancials ? (calculated === null ? dash : formatCurrency(calculated)) : restricted}
                                        subtitle={
                                            calculated === null ? notCalculated : t('calculation_requests.show.kpi.calculated_helper', 'Calculated')
                                        }
                                        icon={<FileText className="text-muted-foreground h-4 w-4" />}
                                    />
                                    <SummaryMetricCard
                                        title={t('calculation_requests.show.kpi.hours', 'Hours spent')}
                                        value={hours === null ? dash : `${hours.toFixed(1)} ${t('calculation_requests.show.kpi.hours_unit', 'h')}`}
                                        subtitle={
                                            hours === null
                                                ? t('calculation_requests.show.kpi.hours_not_logged', 'Not logged yet')
                                                : t('calculation_requests.show.kpi.hours_logged', 'Logged')
                                        }
                                        icon={<Clock className="text-muted-foreground h-4 w-4" />}
                                    />
                                    <SummaryMetricCard
                                        title={t('calculation_requests.show.kpi.complexity', 'Complexity')}
                                        value={
                                            request.complexity
                                                ? settingLabel(t, 'calculation_request_complexities', request.complexity) || request.complexity
                                                : dash
                                        }
                                        icon={<BarChart3 className="text-muted-foreground h-4 w-4" />}
                                    />
                                    <SummaryMetricCard
                                        title={t('calculation_requests.show.kpi.status', 'Status')}
                                        value={statusLabel}
                                        subtitle={t(`calculation_requests.stages.${request.stage}`, request.stage)}
                                        icon={<CheckCircle2 className="text-muted-foreground h-4 w-4" />}
                                        intent={request.stage === 'done' ? 'success' : 'neutral'}
                                    />
                                </div>

                                {/*
                                 * The request's dates, in the order they happen, on one line under
                                 * the KPIs. Deliberately a step down in type size from the cards
                                 * above — these are context, not headline figures.
                                 */}
                                <div className="bg-card grid grid-cols-2 divide-y overflow-hidden rounded-xl border sm:grid-cols-4 sm:divide-x sm:divide-y-0">
                                    {dateStrip.map((item) => (
                                        <div key={item.label} className="px-4 py-2.5">
                                            <div className="text-muted-foreground text-[11px] font-medium">{item.label}</div>
                                            <div className="mt-0.5 flex items-center gap-1.5 text-xs font-medium">
                                                {item.value}
                                                {item.badge}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/*
                                 * Comments run alongside the specification rather than under it, as on the
                                 * lead page. The card is taken out of flow on desktop so a long thread can
                                 * never stretch the row taller than the cards beside it — it fills the
                                 * wrapper, whose height comes from those cards (min 26rem).
                                 */}
                                <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                                    <div className="relative lg:min-h-[26rem]">
                                        <Card className="flex max-h-[67dvh] flex-col gap-4 overflow-hidden pb-0 lg:absolute lg:inset-0 lg:max-h-none">
                                            <CardHeader className="pb-3">
                                                <CardTitle className="text-base">
                                                    {t('calculation_requests.show.tabs.comments', 'Comments')}
                                                </CardTitle>
                                            </CardHeader>
                                            <CardContent className="flex min-h-0 flex-1 flex-col px-0">
                                                <UniversalComments
                                                    model="CalculationRequest"
                                                    entityId={request.id}
                                                    users={users as never}
                                                    currentUser={currentUser}
                                                    layout="container"
                                                />
                                            </CardContent>
                                        </Card>
                                    </div>

                                    <div className="space-y-4 lg:col-span-2">
                                        {/* Technical specification — two balanced columns of label/value */}
                                        <Card>
                                            <CardHeader className="flex flex-row items-center justify-between gap-3 pb-3">
                                                <CardTitle className="text-base">
                                                    {t('calculation_requests.show.sections.specification', 'Technical specification')}
                                                </CardTitle>
                                                {canEditSpecification ? (
                                                    <Button
                                                        type="button"
                                                        variant="outline"
                                                        size="sm"
                                                        className="gap-2"
                                                        onClick={() => setEditSpecOpen(true)}
                                                    >
                                                        <Pencil className="h-3.5 w-3.5" />
                                                        {t('common.edit', 'Edit')}
                                                    </Button>
                                                ) : null}
                                            </CardHeader>
                                            <CardContent>
                                                <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-2">
                                                    {specRows.map(([label, value]) => (
                                                        <div key={label} className="flex flex-col gap-0.5">
                                                            <dt className="text-muted-foreground text-xs font-medium">{label}</dt>
                                                            <dd className={value ? 'text-sm font-medium' : 'text-muted-foreground text-sm'}>
                                                                {value || dash}
                                                            </dd>
                                                        </div>
                                                    ))}
                                                </dl>

                                                {request.notes ? (
                                                    <div className="mt-5 border-t pt-4">
                                                        <p className="text-muted-foreground mb-1 text-xs font-medium">
                                                            {t('calculation_requests.show.sections.notes', 'Notes')}
                                                        </p>
                                                        <p className="text-sm whitespace-pre-wrap">{request.notes}</p>
                                                    </div>
                                                ) : null}
                                            </CardContent>
                                        </Card>

                                        {/* Calculation result — the outcome in context, not a KPI repeat */}
                                        {request.stage === 'done' || calculated !== null ? (
                                            <Card>
                                                <CardHeader className="pb-3">
                                                    <CardTitle className="text-base">
                                                        {t('calculation_requests.show.sections.result', 'Calculation result')}
                                                    </CardTitle>
                                                </CardHeader>
                                                <CardContent className="space-y-4">
                                                    <dl className="grid gap-x-8 gap-y-4 sm:grid-cols-3">
                                                        <div className="flex flex-col gap-0.5">
                                                            <dt className="text-muted-foreground text-xs font-medium">
                                                                {t('calculation_requests.tables.calculated_amount', 'Calculated')}
                                                            </dt>
                                                            <dd className="text-sm font-semibold">
                                                                {canViewFinancials
                                                                    ? calculated === null
                                                                        ? dash
                                                                        : formatCurrency(calculated)
                                                                    : restricted}
                                                            </dd>
                                                        </div>
                                                        <div className="flex flex-col gap-0.5">
                                                            <dt className="text-muted-foreground text-xs font-medium">
                                                                {t('calculation_requests.tables.hours_spent', 'Hours')}
                                                            </dt>
                                                            <dd className="text-sm font-medium">{hours === null ? dash : hours.toFixed(1)}</dd>
                                                        </div>
                                                        <div className="flex flex-col gap-0.5">
                                                            <dt className="text-muted-foreground text-xs font-medium">
                                                                {t('calculation_requests.tables.complexity', 'Complexity')}
                                                            </dt>
                                                            <dd>
                                                                {request.complexity ? (
                                                                    <ComplexityBadge value={request.complexity} options={options.complexities} />
                                                                ) : (
                                                                    <span className="text-muted-foreground text-sm">{dash}</span>
                                                                )}
                                                            </dd>
                                                        </div>
                                                    </dl>

                                                    {request.completion_notes ? (
                                                        <p className="border-t pt-4 text-sm whitespace-pre-wrap">{request.completion_notes}</p>
                                                    ) : null}
                                                </CardContent>
                                            </Card>
                                        ) : null}
                                    </div>
                                </div>
                            </TabsContent>

                            <TabsContent value="attachments" className="mt-0">
                                <CalculationAttachmentsPanel request={request} canUpload={canUpdate} canDelete={canUpdate} />
                            </TabsContent>

                            <TabsContent value="activity" className="mt-0">
                                <Card>
                                    <CardContent className="pt-6">
                                        {events.length === 0 ? (
                                            <p className="text-muted-foreground text-sm">
                                                {t('calculation_requests.show.empty_activity', 'No activity recorded yet.')}
                                            </p>
                                        ) : (
                                            <ul className="space-y-3">
                                                {events.map((event) => (
                                                    <li key={event.id} className="flex items-start justify-between gap-3 border-b pb-3 last:border-0">
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium">{event.event ?? dash}</p>
                                                            <p className="text-muted-foreground text-xs">{userName(event.actor)}</p>
                                                        </div>
                                                        <span className="text-muted-foreground shrink-0 text-xs">
                                                            {event.created_at ? formatDateForDisplay(event.created_at) : ''}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </CardContent>
                                </Card>
                            </TabsContent>
                        </div>

                        {/* ── Sidebar: who owns it, and when it moved ───────────────── */}
                        <aside className="space-y-4">
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-base">{t('calculation_requests.show.sections.overview', 'Overview')}</CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-3 text-sm">
                                    <Field label={t('calculation_requests.tables.status', 'Status')}>
                                        <InlineCalcStatusCell
                                            value={request.status}
                                            statuses={options.statuses}
                                            readOnly={!canUpdate}
                                            onUpdate={canUpdate ? patchStatus : undefined}
                                            className="px-0"
                                        />
                                    </Field>
                                    <Field label={t('calculation_requests.tables.assignee', 'Engineer')}>
                                        <PersonSelect
                                            value={request.assigned_to_user_id}
                                            onValueChange={(userId) => assign(userId)}
                                            people={options.engineers}
                                            disabled={!canAssign}
                                            variant="inline"
                                            allowClear={false}
                                            showUnassignedOption
                                            className="-ml-1.5"
                                            labels={{
                                                placeholder: t('calculation_requests.form.fields.assignee_unassigned', 'Unassigned'),
                                                search: t('calculation_requests.form.fields.assignee_search', 'Search engineers…'),
                                                empty: t('calculation_requests.form.fields.assignee_empty', 'No engineers found.'),
                                                peopleHeading: t('calculation_requests.form.fields.assignee_engineers', 'Engineers'),
                                            }}
                                        />
                                    </Field>
                                    <Field label={t('calculation_requests.contributors.title', 'Contributors')}>
                                        <PersonSelect
                                            multiple
                                            values={contributorIds}
                                            onValuesChange={syncContributors}
                                            people={contributorPeople}
                                            disabled={!canManageContributors}
                                            variant="inline"
                                            className="-ml-1.5"
                                            labels={{
                                                placeholder: t('calculation_requests.contributors.add_placeholder', 'Select a colleague'),
                                                search: t('calculation_requests.contributors.search', 'Search colleagues…'),
                                                empty: t('calculation_requests.contributors.empty_candidates', 'No colleagues left to add.'),
                                                peopleHeading: t('calculation_requests.contributors.heading', 'Colleagues'),
                                                selected: t('calculation_requests.contributors.selected', 'contributors'),
                                            }}
                                        />
                                    </Field>
                                    <Field label={t('calculation_requests.tables.company', 'Customer')}>{request.company?.name ?? dash}</Field>
                                    {/* Where this calculation is attached — set from the lead and project pages. */}
                                    <Field label={t('calculation_requests.tables.lead', 'Lead')}>
                                        {request.lead ? (
                                            <Link href={`/leads/${request.lead.id}`} className="text-primary hover:underline">
                                                {request.lead.lead_number || request.lead.name || request.lead.id}
                                            </Link>
                                        ) : (
                                            <span className="text-muted-foreground">{notLinked}</span>
                                        )}
                                    </Field>
                                    <Field label={t('calculation_requests.tables.project', 'Project')}>
                                        {request.project ? (
                                            <Link href={`/projects/${request.project.id}`} className="text-primary hover:underline">
                                                {[request.project.contract_number, request.project.contract_name].filter(Boolean).join(' - ') ||
                                                    request.project.id}
                                            </Link>
                                        ) : (
                                            <span className="text-muted-foreground">{notLinked}</span>
                                        )}
                                    </Field>
                                    <Field label={t('calculation_requests.tables.project_phase', 'Phase')}>
                                        {request.project_phase ? (
                                            <PhaseBadge value={request.project_phase} options={options.phases} />
                                        ) : (
                                            <span className="text-muted-foreground">{dash}</span>
                                        )}
                                    </Field>
                                    <Field label={t('calculation_requests.tables.submitted_by', 'Created by')}>
                                        {userName(request.submitted_by)}
                                    </Field>
                                    <Field label={t('calculation_requests.show.timeline.accepted', 'Accepted')}>
                                        {request.accepted_by
                                            ? `${userName(request.accepted_by)} · ${formatDateForDisplay(request.accepted_at ?? '')}`
                                            : dash}
                                    </Field>
                                </CardContent>
                            </Card>
                        </aside>
                    </div>
                </Tabs>
            </div>

            <CompleteCalculationModal request={request} options={options} open={completeOpen} onOpenChange={setCompleteOpen} />

            {canEditSpecification ? <EditCalculationSpecSheet request={request} open={editSpecOpen} onOpenChange={setEditSpecOpen} /> : null}

            <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('calculation_requests.show.delete.confirm_title', 'Delete calculation request')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {t('calculation_requests.show.delete.confirm_description', 'Delete :number? This cannot be undone.').replace(
                                ':number',
                                request.request_number ?? '',
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('calculation_requests.show.delete.cancel', 'Cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => router.delete(`/calculation-requests/${request.id}`)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {t('calculation_requests.show.delete.confirm_button', 'Delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </AppLayout>
    );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-muted-foreground text-xs font-medium">{label}</span>
            <span className="text-sm">{children}</span>
        </div>
    );
}

/**
 * The first configured status in a stage. Statuses are admin-managed, so the action
 * buttons resolve their target from settings rather than hardcoding a value.
 */
function nextStatusForStage(options: CalculationRequestOptions, stage: string): string {
    return options.statuses.find((status) => status.stage === stage)?.value ?? '';
}
